import React, { useState } from 'react';
import { API_BASE } from '../../config';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
function ManageTrainerReportsModal({ reports, responses, onClose, fetchWithAuth, onRefresh }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [activeView, setActiveView] = useState('reports'); // 'reports' or 'responses'
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDeleteReport = async (reportId) => {
    try {
      await fetchWithAuth(`${API_BASE}/api/trainer-reports/${reportId}`, {
        method: 'DELETE'
      });
      setDeleteConfirm(null);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete report:', error);
      alert('Failed to delete report');
    }
  };

  const markResponseRead = async (responseId) => {
    try {
      await fetchWithAuth(`${API_BASE}/api/trainer-reports/responses/${responseId}/read`, {
        method: 'POST'
      });
      onRefresh();
    } catch (error) {
      console.error('Failed to mark response as read:', error);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content mtr-modal" >
        <div className="modal-header trm-gradientHeader">
          <h3>🏋️ Manage Trainer Reports</h3>
          <button className="modal-close text-white" onClick={onClose}>&times;</button>
        </div>

        <div className="p-5">
          {/* Tab Navigation */}
          <div className="trm-tabBar">
            <button
              onClick={() => setActiveView('reports')}
              className={`trm-tab ${activeView === 'reports' ? 'trm-tab--active' : ''}`}
            >
              📄 Sent Reports ({reports.length})
            </button>
            <button
              onClick={() => setActiveView('responses')}
              className={`trm-tab trm-tab--withBadge ${activeView === 'responses' ? 'trm-tab--active' : ''}`}
            >
              💬 Responses ({responses.length})
              {responses.filter(r => !r.is_read).length > 0 && (
                <span className="trm-unreadBadge">
                  {responses.filter(r => !r.is_read).length}
                </span>
              )}
            </button>
          </div>

          {/* Reports List */}
          {activeView === 'reports' && (
            <div>
              {reports.length === 0 ? (
                <p className="trm-emptyState">
                  No reports sent yet.
                </p>
              ) : (
                reports.map(report => (
                  <div key={report.id} className="trm-reportCard">
                    <div className="flex-between-start">
                      <div className="flex-1">
                        <h4 className="m-0-0-8-0">{report.report_title}</h4>
                        <p className="trm-metaText">
                          <strong>Trainers:</strong> {report.trainer_names?.join(', ') || 'N/A'}
                        </p>
                        <p className="trm-dateText">
                          Created: {new Date(report.created_at).toLocaleString()}
                        </p>
                        {report.email_sent_at && (
                          <p className="trm-dateText">
                            Emailed: {new Date(report.email_sent_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="trm-cardSidebar">
                        <span className="trm-statusBadge" style={{ backgroundColor: report.generation_status === 'sent' ? 'var(--color-success-light)' :
                                          report.generation_status === 'failed' ? 'var(--color-error-light)' : 'var(--color-warning-light)', color: report.generation_status === 'sent' ? 'var(--color-success)' :
                                 report.generation_status === 'failed' ? 'var(--color-error)' : '#92400E' }}>
                          {report.generation_status}
                        </span>
                        <div className="trm-actionButtons">
                          <button
                            onClick={() => setSelectedReport(selectedReport?.id === report.id ? null : report)}
                            className="btn btn-secondary btn-sm"
                          >
                            {selectedReport?.id === report.id ? 'Hide' : 'View'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(report.id)}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded View */}
                    {selectedReport?.id === report.id && (
                      <div className="trm-expandedDetail">
                        <p className="m-0-0-8-0"><strong>Report ID:</strong> {report.id}</p>
                        <p className="m-0-0-8-0"><strong>Status:</strong> {report.generation_status}</p>
                        <p className="m-0-0-8-0"><strong>Progress:</strong> {report.progress_percentage}%</p>
                        <p className="m-0-0-8-0"><strong>Sent To:</strong> {report.trainer_names?.join(', ') || 'N/A'}</p>
                        {report.email_sent_at && (
                          <p className="m-0"><strong>Email Sent:</strong> {new Date(report.email_sent_at).toLocaleString()}</p>
                        )}
                        <p className="trm-hipaaNote">
                          Note: PDF was securely deleted after emailing per HIPAA compliance.
                        </p>
                      </div>
                    )}

                    {/* Delete Confirmation */}
                    {deleteConfirm === report.id && (
                      <div className="trm-deleteConfirm">
                        <p className="trm-deleteWarning">
                          Are you sure you want to delete this report?
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDeleteReport(report.id)}
                            className="trm-confirmDeleteBtn"
                          >
                            Yes, Delete
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="trm-cancelBtn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Responses List */}
          {activeView === 'responses' && (
            <div>
              {responses.length === 0 ? (
                <p className="trm-emptyState">
                  No responses received yet.
                </p>
              ) : (
                responses.map(response => (
                  <div
                    key={response.id}
                    className="trm-responseCard" style={{ backgroundColor: response.is_read ? 'var(--brand-surface-elevated)' : 'rgba(30, 58, 95, 0.08)', border: response.is_read ? 'none' : '2px solid var(--brand-primary)' }}
                    onClick={() => {
                      setSelectedResponse(response);
                      if (!response.is_read) {
                        markResponseRead(response.id);
                      }
                    }}
                  >
                    <div className="flex-between-start">
                      <div>
                        <h4 className="trm-responseHeading">
                          Response from {response.trainer_name}
                        </h4>
                        <p className="trm-metaText">
                          {response.trainer_type}
                          {response.trainer_certification && ` • ${response.trainer_certification}`}
                        </p>
                        <p className="trm-responsePreview">
                          {response.response_text.substring(0, 100)}
                          {response.response_text.length > 100 ? '...' : ''}
                        </p>
                      </div>
                      {!response.is_read && (
                        <span className="trm-newBadge">
                          NEW
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Response Detail Modal */}
        {selectedResponse && (
          <div className="modal-overlay" style={{zIndex: 1002}}>
            <div className="modal-content modal-content-md">
              <div className="trm-detailHeader">
                <div>
                  <h3>Response from {selectedResponse.trainer_name}</h3>
                  <p className="trm-detailMeta">
                    {selectedResponse.trainer_type}
                    {selectedResponse.trainer_certification && ` • ${selectedResponse.trainer_certification}`}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedResponse(null)}
                  className="trm-detailCloseBtn"
                >
                  ×
                </button>
              </div>
              <div className="trm-detailContent">
                <p className="line-height-16">{selectedResponse.response_text}</p>

                {selectedResponse.recommendations && (
                  <div className="trm-recsContainer">
                    <h4 className="trm-recsHeading">Recommendations</h4>
                    <pre className="trm-recsText">
                      {JSON.stringify(selectedResponse.recommendations, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              <div className="trm-detailFooter">
                <button
                  onClick={() => setSelectedResponse(null)}
                  className="btn btn-primary w-full"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="trm-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManageTrainerReportsModal;
